import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  BellOff, 
  Mail, 
  Smartphone, 
  Clock,
  Save,
  Settings,
  CheckCircle,
  AlertCircle,
  X
} from 'lucide-react';
import toast from '../../utils/toast';

const NotificationSettings = ({ isOpen, onClose }) => {
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quietHours, setQuietHours] = useState({
    start: '',
    end: '',
    enabled: false
  });

  const notificationTypes = [
    { key: 'new_lead', label: 'New Leads', description: 'When new leads are captured' },
    { key: 'new_trip', label: 'New Trips', description: 'When trips are created' },
    { key: 'questionnaire_submitted', label: 'Questionnaire Completed', description: 'When clients complete questionnaires' },
    { key: 'lead_converted', label: 'Lead Converted', description: 'When leads become clients' },
    { key: 'new_commission', label: 'New Commissions', description: 'When commissions are added' },
    { key: 'payment_processed', label: 'Payments', description: 'When payments are processed' },
    { key: 'system', label: 'System Updates', description: 'Important system notifications' }
  ];

  useEffect(() => {
    if (isOpen) {
      fetchPreferences();
    }
  }, [isOpen]);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/notifications/preferences`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch preferences');
      }

      const data = await response.json();
      
      // Initialize preferences with defaults if not set
      const initializedPreferences = notificationTypes.map(type => {
        const existing = data.preferences?.find(p => p.notification_type === type.key);
        return existing || {
          notification_type: type.key,
          email_enabled: true,
          in_app_enabled: true,
          push_enabled: false
        };
      });

      setPreferences(initializedPreferences);

      // Set quiet hours if available
      const firstPreference = data.preferences?.[0];
      if (firstPreference?.quiet_hours_start && firstPreference?.quiet_hours_end) {
        setQuietHours({
          start: firstPreference.quiet_hours_start,
          end: firstPreference.quiet_hours_end,
          enabled: true
        });
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = (type, field, value) => {
    setPreferences(prev => 
      prev.map(pref => 
        pref.notification_type === type 
          ? { ...pref, [field]: value }
          : pref
      )
    );
  };

  const savePreferences = async () => {
    try {
      setSaving(true);
      
      const preferencesToSave = preferences.map(pref => ({
        ...pref,
        quiet_hours_start: quietHours.enabled ? quietHours.start : null,
        quiet_hours_end: quietHours.enabled ? quietHours.end : null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }));

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/notifications/preferences`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ preferences: preferencesToSave })
      });

      if (!response.ok) {
        throw new Error('Failed to save preferences');
      }

      toast.success('Notification preferences saved successfully');
      onClose();
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const toggleAll = (field, value) => {
    setPreferences(prev => 
      prev.map(pref => ({ ...pref, [field]: value }))
    );
  };

  if (!isOpen) return null;

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div className="flex gap-3 items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Settings className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Notification Settings</h2>
              <p className="text-sm text-gray-600">Customize how you receive notifications</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 rounded-lg hover:text-gray-600 hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Global Controls */}
          <div className="p-4 bg-gray-50 rounded-xl">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Global Settings</h3>
            <div className="grid grid-cols-3 gap-4">
              <button
                onClick={() => toggleAll('in_app_enabled', true)}
                className="flex gap-2 justify-center items-center p-3 text-green-700 bg-green-50 rounded-lg transition-colors hover:bg-green-100"
              >
                <CheckCircle className="w-4 h-4" />
                Enable All
              </button>
              <button
                onClick={() => toggleAll('in_app_enabled', false)}
                className="flex gap-2 justify-center items-center p-3 text-red-700 bg-red-50 rounded-lg transition-colors hover:bg-red-100"
              >
                <BellOff className="w-4 h-4" />
                Disable All
              </button>
              <button
                onClick={() => toggleAll('email_enabled', true)}
                className="flex gap-2 justify-center items-center p-3 text-blue-700 bg-blue-50 rounded-lg transition-colors hover:bg-blue-100"
              >
                <Mail className="w-4 h-4" />
                Enable Email
              </button>
            </div>
          </div>

          {/* Quiet Hours */}
          <div className="p-4 bg-gray-50 rounded-xl">
            <h3 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900">
              <Clock className="w-5 h-5" />
              Quiet Hours
            </h3>
            <div className="flex gap-4 items-center">
              <label className="flex gap-2 items-center">
                <input
                  type="checkbox"
                  checked={quietHours.enabled}
                  onChange={(e) => setQuietHours(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Enable quiet hours</span>
              </label>
              {quietHours.enabled && (
                <div className="flex gap-2 items-center">
                  <input
                    type="time"
                    value={quietHours.start}
                    onChange={(e) => setQuietHours(prev => ({ ...prev, start: e.target.value }))}
                    className="px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="time"
                    value={quietHours.end}
                    onChange={(e) => setQuietHours(prev => ({ ...prev, end: e.target.value }))}
                    className="px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              During quiet hours, only urgent notifications will be sent
            </p>
          </div>

          {/* Notification Types */}
          <div>
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Notification Types</h3>
            <div className="space-y-3">
              {notificationTypes.map(type => {
                const preference = preferences.find(p => p.notification_type === type.key);
                return (
                  <div key={type.key} className="p-4 bg-white rounded-lg border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{type.label}</h4>
                        <p className="text-sm text-gray-600">{type.description}</p>
                      </div>
                      <div className="flex gap-4 items-center ml-4">
                        {/* In-App Notifications */}
                        <label className="flex gap-2 items-center">
                          <Bell className="w-4 h-4 text-gray-500" />
                          <input
                            type="checkbox"
                            checked={preference?.in_app_enabled || false}
                            onChange={(e) => updatePreference(type.key, 'in_app_enabled', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">In-App</span>
                        </label>

                        {/* Email Notifications */}
                        <label className="flex gap-2 items-center">
                          <Mail className="w-4 h-4 text-gray-500" />
                          <input
                            type="checkbox"
                            checked={preference?.email_enabled || false}
                            onChange={(e) => updatePreference(type.key, 'email_enabled', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">Email</span>
                        </label>

                        {/* Push Notifications */}
                        <label className="flex gap-2 items-center">
                          <Smartphone className="w-4 h-4 text-gray-500" />
                          <input
                            type="checkbox"
                            checked={preference?.push_enabled || false}
                            onChange={(e) => updatePreference(type.key, 'push_enabled', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">Push</span>
                        </label>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 justify-end items-center p-6 bg-gray-50 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={savePreferences}
            disabled={saving}
            className="flex gap-2 items-center px-6 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Preferences
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
